// FILE: routeGuards.ts
// Purpose: Send a user away from a route whose section they may not see.
// Layer: Route support
// Exports: useRedirectWithoutGlobalPermission

import type { GlobalPermission } from "@taskin-code/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { useAuthStore } from "./authStore";

/**
 * Redirect to `to` when the signed-in user lacks `permission`.
 *
 * The counterpart of hiding a section from the sidebar: an entry point removed
 * from the menu must not stay reachable by typing its URL. The server is still
 * the boundary — every action behind these routes is refused independently —
 * so this exists to avoid parking someone on a screen whose every control
 * fails, not to keep them out of data.
 *
 * Deliberately waits for `status === "authenticated"`. Redirecting while the
 * store is still hydrating would bounce a user who does hold the permission,
 * and an unauthenticated visitor is already being sent to /login by the root
 * route.
 *
 * Returns whether the route may render, so the caller can hold its content
 * back during the frame before the navigation lands.
 */
export function useRedirectWithoutGlobalPermission(
  permission: GlobalPermission,
  to: string,
): boolean {
  const navigate = useNavigate();
  const status = useAuthStore((state) => state.status);
  const allowed = useAuthStore((state) => state.globalPermissions.has(permission));
  const settled = status === "authenticated";

  useEffect(() => {
    if (!settled || allowed) {
      return;
    }
    void navigate({ to, replace: true });
  }, [allowed, navigate, settled, to]);

  return !settled || allowed;
}
